import { useQuery } from '@tanstack/react-query';
import { api } from '../../../api/client';
import { TEST_CODES, METHODS } from './trainingData';

// ─────────────────────────────────────────────────────────────────────────────
// QA/QC → Training Records — Test and Method lookups.
//
// The record dialog's Test and Method fields are CHECKED combos: the value must
// come from the lab's own master data, never free text (design review
// 2026-08-14). Both lists already exist behind the migrated Test Code screen, so
// this consumes THOSE endpoints rather than adding a parallel lookup
// (CLAUDE.md standing rule 8 / §19.2):
//
//   GET /api/v1/test-methods/test-names → string[]   (DISTINCT dbo.TestMethod.TestName)
//   GET /api/v1/test-methods/methods    → { id, name, number }[]  (dbo.Method)
//
// Reference data that changes rarely, so it is cached for the session rather
// than refetched every time the dialog opens.
// ─────────────────────────────────────────────────────────────────────────────

/** Shape of one row from `GET /api/v1/test-methods/methods`. */
interface MethodOption { id: string; name: string; number?: string | null }

const STALE_MS = 10 * 60 * 1000;   // reference data — 10 minutes is plenty

/** Test names from the Test Code library (`dbo.TestMethod.TestName`). */
export function useTestNames() {
  return useQuery({
    queryKey: ['training', 'lookup', 'test-names'],
    staleTime: STALE_MS,
    queryFn: async (): Promise<string[]> => {
      const res = await api.get('/api/v1/test-methods/test-names');
      const rows = (res.data?.data ?? []) as string[];
      return rows.filter(Boolean);
    },
  });
}

/** Methods from the Method form (`dbo.Method`).
 *
 *  Labelled by METHOD NUMBER where there is one ("SM 5310 B", "EPA 200.7") —
 *  that is what a training record names, and what the certificate prints. The
 *  name is the fallback for rows that have no number. */
export function useMethodNames() {
  return useQuery({
    queryKey: ['training', 'lookup', 'methods'],
    staleTime: STALE_MS,
    queryFn: async (): Promise<string[]> => {
      const res = await api.get('/api/v1/test-methods/methods');
      const rows = (res.data?.data ?? []) as MethodOption[];
      const labels = rows
        .map(m => (m.number?.trim() ? m.number.trim() : m.name?.trim()))
        .filter((v): v is string => Boolean(v));
      return [...new Set(labels)].sort((a, b) => a.localeCompare(b));
    },
  });
}

/**
 * The options to show for a lookup-backed field.
 *
 * Falls back to the built-in sample list when the lookup has not loaded or came
 * back empty, so the field is never an empty dropdown the user cannot get past —
 * an empty required combo blocks the form completely, which is worse than a
 * short list. `current` is always kept so a value that predates the current
 * master data is never silently dropped.
 */
export function optionsFor(
  loaded: string[] | undefined,
  fallback: string[],
  current: string[],
): string[] {
  const base = loaded && loaded.length > 0 ? loaded : fallback;
  const orphans = current.filter(v => v && !base.includes(v));
  return orphans.length ? [...orphans, ...base] : base;
}

/** The static lists, re-exported so callers pass the right fallback per field. */
export const FALLBACK_TESTS = TEST_CODES;
export const FALLBACK_METHODS = METHODS;
